import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";

export default function OrderSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const session_id = params.get("session_id");
    if (!session_id) { setState("failed"); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${session_id}`);
        const ps = res.data?.payment_status;
        setDetails(res.data);
        if (ps === "paid") { setState("paid"); return; }
        if (res.data?.status === "expired") { setState("expired"); return; }
        if (attempts.current >= 8) { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= 8) setState("failed");
        else setTimeout(poll, 2500);
      }
    };
    poll();
  }, [location.search]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid="order-success-page">
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-xl w-full bg-[#FAF7F2] p-10 sm:p-12 text-center" style={{ borderRadius: 4 }}>
          {state === "checking" && (
            <>
              <Loader2 className="animate-spin mx-auto text-[#B8973A]" size={28} />
              <p className="mt-6 italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }} data-testid="order-status">
                Confirming your order…
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <p className="text-[#B8973A] text-3xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</p>
              <p className="label-ui text-[11px] text-[#B8973A] mb-4">Thank you</p>
              <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Your book is on its way.
              </h1>
              <Ornament className="my-8" />
              <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
                {details?.edition_name} · £{(details?.amount_total/100).toFixed(2)} {details?.currency?.toUpperCase()}
              </p>
              <p className="text-[#8C8278] text-sm mt-4">
                We've received your order and will write to you with confirmation and tracking shortly.
              </p>
              <button onClick={() => navigate("/dashboard")} className="btn-primary mt-10" data-testid="back-to-dashboard-btn">
                Back to your family
              </button>
            </>
          )}
          {state === "expired" && (
            <>
              <h1 className="text-3xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>The payment session has expired.</h1>
              <p className="mt-4 italic text-[#8C8278]">Please try again — your responses are still in your account.</p>
              <button onClick={() => navigate(-2)} className="btn-primary mt-8" data-testid="retry-order-btn">Try again</button>
            </>
          )}
          {state === "failed" && (
            <>
              <h1 className="text-3xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>We couldn't confirm your order.</h1>
              <p className="mt-4 italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                If you've been charged, please email us — we'll sort it out personally.
              </p>
              <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8" data-testid="back-home-btn">Back to home</button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
